'use es6';

import { isEmpty } from 'underscore';
/**
 *  Deeply flattens folder contents tree returned from `folder/:folderId/contents`
 *
 *  @param {object} folderContents
 *  ```
 *  DesignFolderContents {
 *    folder: DesignFolder,
 *    files: [DesignFolderFile],
 *    subfolders: [DesignFolderContents],
 *    folderMetadata: DesignFolderMetadata
 *  }
 *  ```
 *
 *  @returns {object} foldersById
 *  ```
 *  foldersById {
 *    [DesignFolder.id]: DesignFolder,
 *    [DesignFolder.id]: DesignFolder,
 *    ...
 *  }
 *  ```
 */

export function getFoldersByIdFromFolderContentsTree(folderContents) {
  if (!folderContents || isEmpty(folderContents)) return {};
  let foldersById = {};
  let folderContentsQueue = [folderContents];

  while (folderContentsQueue.length !== 0) {
    const currFolderContents = folderContentsQueue.shift();
    const {
      folder,
      readOnly
    } = currFolderContents;

    if (folder && !!folder.id) {
      foldersById = Object.assign({}, foldersById, {
        [folder.id]: Object.assign({}, folder, {
          readOnly
        })
      });
    } //  Enqueue nested subfolders


    folderContentsQueue = [...folderContentsQueue, ...(currFolderContents.subfolders || [])];
  }

  return foldersById;
}
/**
 *  Gets folderMetadata from folder contents object returned from `folder/:folderId/contents`
 *
 *  **  DOES NOT TRAVERSE SUBFOLDERS **
 *
 *  @param {object} folderContents
 *  ```
 *  DesignFolderContents {
 *    folder: DesignFolder,
 *    files: [DesignFolderFile],
 *    subfolders: [DesignFolderContents],
 *    folderMetadata: DesignFolderMetadata
 *  }
 *  ```
 *
 *  @returns {object} folderMetadataById
 *  ```
 *  folderMetadataById {
 *    [DesignFolderMetadata.id]: DesignFolderMetadata,
 *  }
 *  ```
 */

export function getFolderMetadataByIdFromFolderContentsObject(folderContents) {
  if (!folderContents || isEmpty(folderContents)) return {};
  const {
    folder,
    folderMetadata
  } = folderContents;
  if (!folder || !folder.id || !folderMetadata || !folderMetadata.id) return {};
  return {
    [folder.id]: folderMetadata
  };
}
/**
 *  Flattens folder metadata from folder contents tree returned from `folder/:folderId/contents`
 *
 *  @param {object} folderContents
 *  ```
 *  DesignFolderContents {
 *    folder: DesignFolder,
 *    files: [DesignFolderFile],
 *    subfolders: [DesignFolderContents],
 *    folderMetadata: DesignFolderMetadata
 *  }
 *  ```
 *
 *  @returns {object} folderMetadataById
 *  ```
 *  folderMetadataById {
 *    [DesignFolderMetadata.id]: DesignFolderMetadata,
 *    [DesignFolderMetadata.id]: DesignFolderMetadata,
 *    ...
 *  }
 *  ```
 */

export function getFolderMetadataByIdFromFolderContentsTree(folderContents) {
  if (!folderContents || isEmpty(folderContents)) return {};
  let folderMetadataById = {};
  let folderContentsQueue = [folderContents];

  while (folderContentsQueue.length !== 0) {
    const currFolderContents = folderContentsQueue.shift();
    folderMetadataById = Object.assign({}, folderMetadataById, {}, getFolderMetadataByIdFromFolderContentsObject(currFolderContents)); //  Enqueue nested subfolders

    folderContentsQueue = [...folderContentsQueue, ...(currFolderContents.subfolders || [])];
  }

  return folderMetadataById;
}
/**
 * @param {String|Number} folderId
 * @param {Array<String|Number} files
 * @param {Array<String|Number} subfolders
 */

export function createFolderContents(folderId, files = [], subfolders = []) {
  // Just in case someone passes null e.g. only to add subfolders
  files = files || [];
  subfolders = subfolders || [];
  return {
    [folderId]: {
      files,
      subfolders
    }
  };
}
/**
 *  Flattens folder contents from folder contents object returned from `folder/:folderId/contents`
 *
 *  ** DOES NOT TRAVERSE SUBFOLDERS **
 *
 *  @param {object} folderContents
 *  ```
 *  DesignFolderContents {
 *    folder: DesignFolder,
 *    files: [DesignFolderFile],
 *    subfolders: [DesignFolderContents],
 *    folderMetadata: DesignFolderMetadata
 *  }
 *  ```
 *
 *  @returns {object} folderContentsById
 *  ```
 *  folderContentsById {
 *    [DesignFolderMetadata.id]: {
 *      files: [DesignFolderFile.id],
 *      subfolders: [DesignFolder.id]
 *    }
 *  }
 *  ```
 */

export function getFolderContentsByIdFromFolderContentsObject(folderContents) {
  if (!folderContents || isEmpty(folderContents)) return {};
  const folderId = (folderContents.folder || {}).id;
  if (!folderId) return {};
  const fileIds = (folderContents.files || []).map(file => file.id);
  const folderIds = (folderContents.subfolders || []).filter(subfolderContents => subfolderContents && subfolderContents.folder).map(subfolderContents => (subfolderContents.folder || {}).id);
  return createFolderContents(folderId, fileIds, folderIds);
}